import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Briefcase, BookmarkCheck, Eye, Send } from "lucide-react";

interface KPIData {
  totalApplications: number;
  savedJobs: number;
  recentlyViewed: number;
  activeApplications: number;
}

export default function DashboardKPIs() {
  const [kpis, setKpis] = useState<KPIData>({
    totalApplications: 0,
    savedJobs: 0,
    recentlyViewed: 0,
    activeApplications: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKPIs();
  }, []);

  const fetchKPIs = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch all KPIs in parallel
      const [applicationsRes, savedRes, viewsRes, activeRes] = await Promise.all([
        // Total applications
        supabase
          .from("job_applications")
          .select("id", { count: "exact", head: true })
          .eq("candidate_id", user.id),

        // Saved jobs
        supabase
          .from("saved_jobs")
          .select("id", { count: "exact", head: true })
          .eq("candidate_id", user.id),

        // Recently viewed (last 30 days)
        supabase
          .from("job_views")
          .select("id", { count: "exact", head: true })
          .eq("candidate_id", user.id)
          .gte("viewed_at", new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()),

        // Active applications (not rejected/withdrawn)
        supabase
          .from("job_applications")
          .select("id", { count: "exact", head: true })
          .eq("candidate_id", user.id)
          .not("status", "in", "(rejected,withdrawn)")
      ]);

      setKpis({
        totalApplications: applicationsRes.count || 0,
        savedJobs: savedRes.count || 0,
        recentlyViewed: viewsRes.count || 0,
        activeApplications: activeRes.count || 0,
      });
    } catch (error) {
      console.error("Error fetching KPIs:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Loading...</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-muted animate-pulse rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Applications</CardTitle>
          <Send className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{kpis.totalApplications}</div>
          <p className="text-xs text-muted-foreground">
            {kpis.activeApplications} active
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Active Applications</CardTitle>
          <Briefcase className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{kpis.activeApplications}</div>
          <p className="text-xs text-muted-foreground">
            In progress
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Saved Jobs</CardTitle>
          <BookmarkCheck className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{kpis.savedJobs}</div>
          <p className="text-xs text-muted-foreground">
            Bookmarked opportunities
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Jobs Viewed</CardTitle>
          <Eye className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{kpis.recentlyViewed}</div>
          <p className="text-xs text-muted-foreground">
            Last 30 days
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
